import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { analyticsService } from '../services/analyticsServices';

export const fetchBillingPerformance = createAsyncThunk(
  'analytics/fetchBillingPerformance',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getBillingPerformance(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOperationsEfficiency = createAsyncThunk(
  'analytics/fetchOperationsEfficiency',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getOperationsEfficiency(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchWorkloadIntensity = createAsyncThunk(
  'analytics/fetchWorkloadIntensity',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getWorkloadIntensity(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchScriptVolumeMix = createAsyncThunk(
  'analytics/fetchScriptVolumeMix',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getScriptVolumeMix(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProviderPerformance = createAsyncThunk(
  'analytics/fetchProviderPerformance',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getProviderPerformance(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchReferralProfitability = createAsyncThunk(
  'analytics/fetchReferralProfitability',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getReferralProfitability(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchComplianceAnalytics = createAsyncThunk(
  'analytics/fetchComplianceAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getComplianceAnalytics();
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOrderAnalytics = createAsyncThunk(
  'analytics/fetchOrderAnalytics',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getOrderAnalytics(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSupplyChainMetrics = createAsyncThunk(
  'analytics/fetchSupplyChainMetrics',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getSupplyChainMetrics(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchInsurancePerformance = createAsyncThunk(
  'analytics/fetchInsurancePerformance',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getInsurancePerformance(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMedicationStats = createAsyncThunk(
  'analytics/fetchMedicationStats',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getMedicationStats(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchClaimsDashboard = createAsyncThunk(
  'analytics/fetchClaimsDashboard',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getClaimsDashboard(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchClaimsBillingPerformance = createAsyncThunk(
  'analytics/fetchClaimsBillingPerformance',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getClaimsBillingPerformance(filters);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const syncAllAnalytics = createAsyncThunk(
  'analytics/syncAllAnalytics',
  async (filters, { rejectWithValue }) => {
    try {
      await analyticsService.bulkSyncAll(filters);
      return Date.now();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState: {
    billingPerformance: null,
    operationsEfficiency: null,
    workloadIntensity: null,
    scriptVolumeMix: null,
    providerPerformance: null,
    referralProfitability: null,
    complianceData: null,
    orderAnalytics: null,
    supplyChainData: null,
    insurancePerformance: null,
    claimsDashboard: null,
    medicationStats: null,
    claimsBillingPerformance: null,
    loading: false,
    syncing: false,
    lastSyncedAt: localStorage.getItem('analytics_last_sync') ? parseInt(localStorage.getItem('analytics_last_sync')) : null,
    error: null,
  },
  reducers: {
    clearAnalytics: (state) => {
      state.billingPerformance = null;
      state.operationsEfficiency = null;
      state.workloadIntensity = null;
      state.scriptVolumeMix = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingPerformance.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.billingPerformance = payload?.data || payload;
      })
      .addCase(fetchBillingPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOperationsEfficiency.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOperationsEfficiency.fulfilled, (state, action) => {
        state.loading = false;
        state.operationsEfficiency = action.payload;
      })
      .addCase(fetchOperationsEfficiency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWorkloadIntensity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkloadIntensity.fulfilled, (state, action) => {
        state.loading = false;
        state.workloadIntensity = action.payload;
      })
      .addCase(fetchWorkloadIntensity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchScriptVolumeMix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScriptVolumeMix.fulfilled, (state, action) => {
        state.loading = false;
        state.scriptVolumeMix = action.payload;
      })
      .addCase(fetchScriptVolumeMix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProviderPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProviderPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.providerPerformance = action.payload;
      })
      .addCase(fetchProviderPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchReferralProfitability.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReferralProfitability.fulfilled, (state, action) => {
        state.loading = false;
        state.referralProfitability = action.payload;
      })
      .addCase(fetchReferralProfitability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchComplianceAnalytics.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchComplianceAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.complianceData = action.payload;
      })
      .addCase(fetchComplianceAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrderAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.orderAnalytics = action.payload;
      })
      .addCase(fetchOrderAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSupplyChainMetrics.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupplyChainMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.supplyChainData = action.payload;
      })
      .addCase(fetchSupplyChainMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchInsurancePerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInsurancePerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.insurancePerformance = action.payload;
      })
      .addCase(fetchInsurancePerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchClaimsDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClaimsDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.claimsDashboard = action.payload;
      })
      .addCase(fetchClaimsDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMedicationStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedicationStats.fulfilled, (state, action) => {
        state.loading = false;
        state.medicationStats = action.payload;
      })
      .addCase(fetchMedicationStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchClaimsBillingPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClaimsBillingPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.claimsBillingPerformance = action.payload;
      })
      .addCase(fetchClaimsBillingPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(syncAllAnalytics.pending, (state) => {
        state.syncing = true;
        state.error = null;
      })
      .addCase(syncAllAnalytics.fulfilled, (state, action) => {
        state.syncing = false;
        state.lastSyncedAt = action.payload;
        localStorage.setItem('analytics_last_sync', action.payload.toString());
      })
      .addCase(syncAllAnalytics.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload;
      });
  },
});

export const { clearAnalytics } = analyticsSlice.actions;
export default analyticsSlice.reducer;
